import { codeSplit, Controller, FetchOptions, FetchPageOptions, AnalyticsStore, PageStore, Result } from "bernie-core";
import { PageData, HttpStatus, Request, Response } from "bernie-http";
import { ExtendedContextStore, FlexBffTemplateResponse, Template } from "typings/flexFramework/FlexDefinitions";
import { TemplateStore } from "stores/TemplateStore";
import { CompositionStore } from "src/stores/CompositionStore";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import { StaticMapStore } from "stores/staticMap/StaticMapStore";
import { BlossomCodeSplitter } from "src/components/flexFramework/CodeSplitter/BlossomCodeSplitter";
import { FlexModuleModelStore } from "src/stores/FlexModuleModelStore";
import { FlexSeoWithCompositionResponse } from "utils/seo/domain/FlexSeoWithCompositionResponse";

interface FlexFetchPageOptions extends FetchPageOptions {
  context: ExtendedContextStore;
}

interface WindowWithState extends Window {
  __STATE__: {
    templateStore: TemplateStore;
  };
}

declare var window: WindowWithState;

/**
 * New controller that currently only serves Hotel-Reviews type pages.
 * Currently work in progress in order to work towards the BEXG Tech Strategy.
 * More info here: More info: https://confluence.expedia.biz/display/~mhaddock/Flex%3A+full+adoption+of+Tech+Strategy
 */
export class FlexController implements Controller {
  public pageId: string; // x-page-id is set in the middleware
  public path = "/*.h:id(\\d+).*"; // pick up anything related to specific property (e.g. Hotel-Reviews or Hotel-Information)
  public routeName = "flex-FlexPage";
  public bundles = ["flex-FlexPage"];
  public exact = false;
  public component = codeSplit(async () => {
    const routeComponent = import(/* webpackChunkName: "flex-FlexPage" */ "views/FlexPage") as any;
    let template: Template | undefined;

    // Client-side only
    if (typeof window !== "undefined") {
      const serializedState = window.__STATE__;
      template = serializedState && serializedState.templateStore ? serializedState.templateStore.template : undefined;
    }

    await new BlossomCodeSplitter().loadRequiredCodeSplitBundles({ template });

    return routeComponent;
  });

  /**
   * Gets DUAID and MC1 cookies based on deviceId of incoming context request
   * @returns an array of cookies to be propagated to the flex bff
   */
  public getDeviceIdCookiesFromContextRequest = (req: Request) => {
    const deviceId = req && req.context && req.context.deviceId;
    if (!deviceId) {
      return [];
    }

    return [`DUAID=${deviceId}`, `MC1=GUID=${deviceId.replace(/-/g, "")}`];
  };

  /**
   * Gets set-cookie from the response of Bernie's Platform Context
   * @returns an array of cookies to be propagated to the flex bff
   */
  public getCookiesFromContextResponse = (res: Response | undefined) => {
    if (!res || !res.headers || !res.headers["set-cookie"]) {
      return [];
    }

    // In case set-cookies is only one entry and is a string
    const setCookies = res.headers["set-cookie"];
    if (typeof setCookies === "string") {
      return [setCookies];
    }

    return setCookies;
  };

  public fetch(options: FetchOptions): Promise<Result> {
    const { stores } = options;
    const flexViewModelStore = stores?.get<FlexViewModelStore>("flexViewModel");
    const pageStore = stores?.get<PageStore>("page");
    const contextStore = stores?.get<ExtendedContextStore>("context");
    const templateStore = stores?.get<TemplateStore>("templateStore");
    const compositionStore = stores?.get<CompositionStore>("compositionStore");
    const flexModuleModelStore = stores?.get<FlexModuleModelStore>("flexModuleModelStore");
    const analyticsStore = stores?.get<AnalyticsStore>("analytics");
    const mapStore = stores?.get<StaticMapStore>("staticMap");

    // We get it from the middleware which hijacks the contextStore
    const flexBffResponse = contextStore.viewModelResponse as FlexBffTemplateResponse;
    const footer: string = contextStore.footer;
    delete contextStore.footer;
    delete contextStore.viewModelResponse;

    flexViewModelStore.setupFlexViewModelStore(flexBffResponse, pageStore, contextStore, analyticsStore, footer);
    templateStore.setup(flexBffResponse, contextStore, flexModuleModelStore);
    compositionStore.setup(flexBffResponse, contextStore, flexModuleModelStore, mapStore);

    return Promise.resolve({
      status: HttpStatus.OK,
      next: () => this.fetchDeferredData(options),
    });
  }

  /**
   * A promise to fetch Flex Composition data. Will be executed in parallel with GraphQL queries by bernie-server
   */
  public fetchDeferredData(options: FetchOptions): Promise<any> {
    const templateStore = options.stores.get<TemplateStore>("templateStore");
    const compositionStore = options.stores.get<CompositionStore>("compositionStore");
    const contextStore = options.stores.get<ExtendedContextStore>("context");
    const analyticsStore = options.stores.get<AnalyticsStore>("analytics");
    const pageStore = options.stores.get<PageStore>("page");

    const cookies = this.getDeviceIdCookiesFromContextRequest(options.request).concat(
      this.getCookiesFromContextResponse(options.response)
    );

    const requestHeaders = options.request && options.request.headers;
    const headers = {
      ...requestHeaders,
      cookie: cookies,
    };

    return compositionStore
      .fetchCompositionFromTemplate(templateStore.template, headers, contextStore, analyticsStore, pageStore)
      .then((data) => new FlexSeoWithCompositionResponse(data).overridePageStore(pageStore));
  }

  public fetchPageData(options: FlexFetchPageOptions): Promise<PageData> {
    const bffResponse = options.context.viewModelResponse as FlexBffTemplateResponse;
    const additionalScripts = new BlossomCodeSplitter().getRequiredWebpackChunkNames({
      template: bffResponse.template,
    });
    const seoMetaData = import(/* webpackChunkName: "SeoUtils" */ "utils/seo/SeoUtils").then(({ default: _ }) => {
      return (
        _.buildPageDataWithSeoMetaDataFromFlexBffTemplate(bffResponse) || {
          title: bffResponse.template.title,
        }
      );
    });

    return Promise.resolve(seoMetaData).then((m) => {
      return {
        ...m,
        assetsToIncludeFilter: (assetKey: string) => additionalScripts.includes(assetKey),
      };
    });
  }
}
